import React, { useMemo, useCallback, useEffect, useState } from 'react';
import {
  Eventcalendar as MbscEventCalendar,
  CalendarPrev,
  CalendarNext,
  MbscEventcalendarView,
  createCustomTheme,
} from '@mobiscroll/react';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarToday from '@mui/icons-material/CalendarToday';
import TV from '@mui/icons-material/Tv';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { format, isSameMonth, isSameYear, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { useActivitiesForMonth } from '../queries/activities.queries';
import { useSite } from '@features/sites/queries/use-site.queries';
import { EventType } from '../../../../../../core/enums/event-type';
import { EventColors } from '../../../../../../core/enums/event-type';
import { EventTag } from '@models/eventTag';
import { Announcement } from '@core/interfaces/api';
import { CalendarOverflowWarning } from './calendar-overflow-warning';
import { formatRecurrenceText } from '../utils/recurrence-utils';
import { DeleteEventModal } from './delete-event-modal';
import { useDeleteActivity } from '../hooks/use-delete-activity';

createCustomTheme('goodman', 'material');

export interface StandaloneCalendarProps {
  activeMonth: Date;
  months: Array<Date>;
  printCalendarRestrictions?: {
    monthData: any;
    maxEventRows: any;
    maxChars: { small: number; regular: number } | number;
    eventRowLimits: number;
  };
  onMonthChange: (monthIndex: number) => void;
  onEditActivity: (activity: Announcement) => void;
  // New props for external refresh control
  refreshTrigger?: number; // Increment this to trigger a refresh
  onRefreshReady?: (refetchFn: () => Promise<any>) => void; // Callback to expose refetch function
}

// Extract siteId from URL path like /manage/content/:siteId/activities
function getSiteIdFromUrl(): number | null {
  const path = window.location.pathname;
  const match = path.match(/\/manage\/content\/(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

export function StandaloneCalendar({
  activeMonth,
  months,
  printCalendarRestrictions,
  onMonthChange,
  onEditActivity,
  refreshTrigger,
  onRefreshReady,
}: StandaloneCalendarProps) {
  // Extract siteId from URL
  const siteId = getSiteIdFromUrl();

  // Toast state
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<Announcement | null>(
    null,
  );

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Fetch site data to get timezone
  const {
    data: site,
    isLoading: isSiteLoading,
    error: siteError,
  } = useSite(siteId);

  const timezone = site?.timezone;

  // React Query hooks for data management - called unconditionally
  const {
    data: activities = [],
    isLoading: isActivitiesLoading,
    error: activitiesError,
    refetch: refetchActivities,
  } = useActivitiesForMonth({
    siteId,
    selectedMonth: activeMonth,
    timezone,
  });

  const { deleteActivity, isDeleting } = useDeleteActivity({
    siteId,
    timezone,
    onSuccess: () => {
      refetchActivities();
    },
    onError: (error) => {
      console.error('Error deleting activity:', error);
    },
  });

  // Expose refetch function to parent component
  useEffect(() => {
    if (onRefreshReady && refetchActivities) {
      onRefreshReady(refetchActivities);
    }
  }, [onRefreshReady, refetchActivities]);

  // Handle external refresh triggers
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0 && refetchActivities) {
      refetchActivities();
    }
  }, [refreshTrigger, refetchActivities]);

  // Helper function to show toast
  const showToast = useCallback(
    (message: string, severity: 'success' | 'error') => {
      setToast({ open: true, message, severity });

      // Auto-hide after 3 seconds (matching Angular MatSnackBar default)
      setTimeout(() => {
        setToast((prev) => ({ ...prev, open: false }));
      }, 3000);
    },
    [],
  );

  // Close toast handler
  const handleCloseToast = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  // Transform activities data for Mobiscroll calendar - moved up to ensure consistent hook order
  const calendarEvents = useMemo(() => {
    if (!activities || !timezone) return [];

    return activities
      .filter((activity) => {
        // Filter out invalid activities to prevent runtime errors
        if (
          !activity ||
          !activity.id ||
          !activity.title ||
          !activity.eventStart ||
          !activity.eventEnd
        ) {
          console.warn('Skipping invalid activity:', activity);
          return false;
        }
        return true;
      })
      .map((activity) => {
        try {
          // Properly convert UTC times to site's timezone
          const eventStartDate = parseISO(activity.eventStart.toString());
          const eventEndDate = parseISO(activity.eventEnd.toString());

          // Use utcToZonedTime to correctly convert from UTC to site's timezone
          const startInSiteTimezone = utcToZonedTime(eventStartDate, timezone);
          const endInSiteTimezone = utcToZonedTime(eventEndDate, timezone);

          const location = activity.location
            ? activity.location
            : activity.room?.shortName &&
              activity.room?.shortName !== ' - ' &&
              activity.room?.shortName !== '-'
            ? activity.room?.shortName
            : '';

          // Generate proper recurrence text instead of hardcoded "Recurring Event"
          const recurrenceText = activity?.recurrence
            ? formatRecurrenceText(activity.recurrence)
            : null;

          return {
            id: activity.id, // Use the original activity ID instead of letting Mobiscroll generate one
            start: startInSiteTimezone,
            end: endInSiteTimezone,
            title: activity.title,
            allDay: activity.allDay,
            icon: (activity?.tags?.[0]?.eventTag as EventTag)?.icon,
            eventTag: activity?.tags?.[0]?.eventTag,
            color: EventColors[activity.eventType],
            eventType: activity.eventType,
            location: location,
            room: {
              long: activity.room?.name,
              short: location,
            },
            description: activity.content,
            showOnPrintCalendar: activity.showOnPrintCalendar,
            showOnDigitalSignage: activity.showOnDigitalSignage,
            src: activity, // Original activity data - ensure this is always set
            recurrence: recurrenceText,
          };
        } catch (error) {
          console.error(
            'Error processing activity for calendar:',
            activity,
            error,
          );
          // Return null for invalid activities, which will be filtered out
          return null;
        }
      })
      .filter(Boolean); // Remove any null entries from failed processing
  }, [activities, timezone]);

  // Event handlers - moved up to ensure consistent hook order
  const handlePageLoading = useCallback(
    (event: any) => {
      const chosenMonth = new Date(event.month);
      const month = chosenMonth.getMonth();
      const year = chosenMonth.getFullYear();

      if (
        activeMonth.getMonth() === month &&
        activeMonth.getFullYear() === year
      ) {
        return; // Same month, no navigation needed
      }

      const monthIndex = months.findIndex((value) => {
        return value.getMonth() === month && value.getFullYear() === year;
      });

      if (monthIndex >= 0) {
        onMonthChange(monthIndex);
      }
    },
    [activeMonth, months, onMonthChange],
  );

  const jumpToThisMonth = useCallback(() => {
    const currentMonthIndex = months.findIndex(
      (month) =>
        month.getMonth() === new Date().getMonth() &&
        month.getFullYear() === new Date().getFullYear(),
    );
    if (currentMonthIndex >= 0) {
      onMonthChange(currentMonthIndex);
    }
  }, [months, onMonthChange]);

  const handleDeleteActivity = useCallback(
    (event: any) => {
      // Add null checks to prevent runtime errors
      if (!event?.original?.src) {
        console.error('Invalid event structure for delete:', event);
        showToast('Unable to delete event. Invalid event data.', 'error');
        return;
      }

      const activity = event.original.src;

      // Additional check to ensure activity has required properties
      if (!activity?.id || !activity?.title) {
        console.error('Invalid activity data for delete:', activity);
        showToast('Unable to delete event. Missing activity data.', 'error');
        return;
      }

      // Open the delete confirmation modal
      setActivityToDelete(activity);
      setDeleteModalOpen(true);
    },
    [showToast],
  );

  const handleConfirmDelete = useCallback(
    async (activity: Announcement, scope: 'single' | 'future' | 'all') => {
      try {
        await deleteActivity(activity, scope);
        showToast(`"${activity.title}" deleted.`, 'success');
        setDeleteModalOpen(false);
        setActivityToDelete(null);
      } catch (error) {
        console.error('Failed to delete activity:', error);
        showToast(
          `Error deleting "${activity.title}". Please try again.`,
          'error',
        );
      }
    },
    [deleteActivity, showToast],
  );

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setActivityToDelete(null);
  }, []);

  const handleEditActivity = useCallback(
    (event: any) => {
      // Add null checks to prevent runtime errors
      if (!event?.original?.src) {
        console.error('Invalid event structure for edit:', event);
        showToast('Unable to edit event. Invalid event data.', 'error');
        return;
      }

      const activity = event.original.src;

      // Additional check to ensure activity has required properties
      if (!activity?.id) {
        console.error('Invalid activity data for edit:', activity);
        showToast('Unable to edit event. Missing activity data.', 'error');
        return;
      }

      // Pass the full event object, not just the activity
      onEditActivity(event);
    },
    [onEditActivity, showToast],
  );

  // Header template - moved up to ensure consistent hook order
  const renderHeaderTemplate = useCallback(() => {
    const showPrev = !(
      isSameMonth(activeMonth, months[0]) && isSameYear(activeMonth, months[0])
    );
    const showNext = !(
      isSameMonth(activeMonth, months[24]) &&
      isSameYear(activeMonth, months[24])
    );

    return (
      <>
        {showPrev && <CalendarPrev />}
        <h2 className="mobiscroll-header-month">
          {format(activeMonth, 'MMMM yyyy')}
        </h2>
        {showNext && <CalendarNext />}
        <div className="ml-4">
          <Button onClick={jumpToThisMonth} variant="outlined" color="primary">
            Jump To This Month
          </Button>
        </div>
      </>
    );
  }, [activeMonth, months, jumpToThisMonth]);

  // Agenda template - moved up to ensure consistent hook order
  const renderAgendaListTemplate = useCallback(
    (data: any) => {
      if (isActivitiesLoading) {
        return (
          <div className="w-full h-full min-h-40 flex justify-center items-center">
            <CircularProgress />
          </div>
        );
      }

      if (activitiesError) {
        return (
          <div className="w-full h-full flex justify-center items-center">
            <p className="text-red-600">
              Failed to load activities. Please try again.
            </p>
          </div>
        );
      }
      return (
        <div className="agenda-content-wrapper">
          {data.map((day: any) => (
            <ul key={day.timestamp}>
              <li className="date-header">{day.date}</li>
              {printCalendarRestrictions && (
                <li>
                  <CalendarOverflowWarning
                    events={day.events}
                    printCalendarRestrictions={printCalendarRestrictions}
                    selectedMonth={activeMonth}
                  />
                </li>
              )}
              {day.events
                ?.map((event: any) => {
                  // Add defensive check for event structure
                  if (!event || !event.original) {
                    console.warn('Skipping invalid event in agenda:', event);
                    return null;
                  }

                  return (
                    <li key={event.id || Math.random()}>
                      <div className="mbsc-event">
                        <IconButton
                          className="event-delete"
                          onClick={() => handleDeleteActivity(event)}
                          disabled={isDeleting}
                          sx={{
                            color: '#d32f2f',
                            '&:hover': {
                              backgroundColor: 'rgba(211, 47, 47, 0.08)',
                              color: '#b71c1c',
                            },
                            '&:disabled': {
                              color: 'rgba(211, 47, 47, 0.26)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                        <div
                          className="agenda-wrapper cursor-pointer"
                          onClick={() => handleEditActivity(event)}
                        >
                          <Tooltip
                            title={
                              EventType[event.original?.eventType] || 'Event'
                            }
                          >
                            <div
                              className="mbsc-event-type-indicator"
                              style={{ backgroundColor: event.color }}
                            ></div>
                          </Tooltip>
                          <div className="mbsc-event-time">
                            {event.allDay ? (
                              <div className="mbsc-event-all-day">
                                All Day Event
                              </div>
                            ) : (
                              <>
                                <div className="mbsc-event-start">
                                  {event.start}
                                </div>
                                <div className="mbsc-event-end">
                                  {event.end}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="event-icon-wrapper">
                            <Tooltip
                              title={event.original?.eventTag?.tag || 'Event'}
                            >
                              <img
                                className="event-icon"
                                src={`assets/icons/event-tag/${
                                  event.original?.icon
                                    ? event.original.icon
                                    : 'mobileapp'
                                }.svg`}
                                alt="Event icon"
                              />
                            </Tooltip>
                          </div>

                          <div className="mbsc-event-content">
                            <div className="event-title">
                              {event.original?.recurrence && (
                                <Tooltip
                                  title={`Repeats ${event.original.recurrence}`}
                                >
                                  <span className="material-icons repeat-icon">
                                    refresh
                                  </span>
                                </Tooltip>
                              )}
                              {event.original?.title || 'Untitled Event'}
                              <span className="event-location">
                                {event.original?.room?.long}
                                {event.original?.location &&
                                  event.original.location !==
                                    event.original?.room?.short &&
                                  `, ${event.original.location}`}
                              </span>
                            </div>
                          </div>
                          <div
                            className="event-description w-20 px-2 gap-2"
                            style={{
                              borderLeft: 'solid 2px black',
                            }}
                          >
                            {event.original?.showOnPrintCalendar && (
                              <Tooltip title="On Print Calendar">
                                <CalendarToday />
                              </Tooltip>
                            )}
                            {event.original?.showOnDigitalSignage && (
                              <Tooltip title="On Digital Signage">
                                <TV />
                              </Tooltip>
                            )}
                          </div>
                          <div
                            className="event-description"
                            style={{
                              borderLeft: 'solid 2px black',
                              paddingLeft: event.original?.description
                                ? '10px'
                                : '0',
                            }}
                          >
                            {event.original?.description}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })
                .filter(Boolean)}
            </ul>
          ))}
        </div>
      );
    },
    [
      isActivitiesLoading,
      activitiesError,
      isDeleting,
      handleDeleteActivity,
      handleEditActivity,
      printCalendarRestrictions,
      activeMonth,
    ],
  );

  // Sort events function (keeping existing behavior)
  const sortEvents = useCallback(() => {
    return 1; // Override mobiscroll's default sorting
  }, []);

  const view: MbscEventcalendarView = {
    agenda: { type: 'month', size: 1 },
  };

  // NOW CONDITIONAL RENDERING AFTER ALL HOOKS

  // Show loading state while site is loading
  if (isSiteLoading) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <CircularProgress color="secondary" />
        <span className="ml-2">Loading site information...</span>
      </div>
    );
  }

  // Show error if site loading failed
  if (siteError || !siteId) {
    return (
      <div className="w-full h-full flex justify-center items-center">
        <p className="text-red-600">
          Failed to load site information. Please check the URL and try again.
        </p>
      </div>
    );
  }

  return (
    <>
      <MbscEventCalendar
        data={calendarEvents}
        selectedDate={activeMonth}
        onPageLoading={handlePageLoading}
        renderHeader={renderHeaderTemplate}
        renderAgenda={renderAgendaListTemplate}
        view={view}
        dateFormatLong="DDDD, MMM D, YYYY"
        noEventsText="No scheduled events"
        eventOrder={sortEvents}
      />

      {/* Delete confirmation modal */}
      <DeleteEventModal
        open={deleteModalOpen}
        activity={activityToDelete}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />

      {/* Toast notifications */}
      <div
        className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-50 p-4 rounded shadow-lg cursor-pointer ${
          toast.open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        } transition-opacity duration-300`}
        style={{
          backgroundColor: toast.severity === 'success' ? '#323232' : '#d32f2f',
          color: '#ffffff',
          minWidth: '288px',
          maxWidth: '568px',
          fontSize: '14px',
          fontFamily: 'Gotham Book',
          boxShadow:
            '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
        }}
        onClick={handleCloseToast}
      >
        <p className="m-0">{toast.message}</p>
      </div>
    </>
  );
}
